import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { BUTTON_RADIUS } from 'HubStyleTokens/sizes';
import { CALYPSO, OLAF } from 'HubStyleTokens/colors';
import UIClickable from 'UIComponents/button/UIClickable';
import UIForm from 'UIComponents/form/UIForm';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UISearchInput from 'UIComponents/input/UISearchInput';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIIconButton from 'UIComponents/button/UIIconButton';
import UIFlex from 'UIComponents/layout/UIFlex'; // @ts-expect-error ts-migrate(7016) FIXME: Could not find a declaration file for module '../.... Remove this comment to see the full error message

import { usePrevious } from '../../hooks/usePrevious';
import useForwardedRef from '../../hooks/useForwardedRef';
const SEARCH_HEIGHT = '44px';
const SearchButton = styled(UIIconButton).withConfig({
  displayName: "MobileSearchBar__SearchButton",
  componentId: "oqoj09-0"
})(["border-radius:0 ", " ", " 0 !important;border-left-width:0 !important;width:20% !important;height:", ";"], BUTTON_RADIUS, BUTTON_RADIUS, SEARCH_HEIGHT);
const SearchInput = styled(UISearchInput).withConfig({
  displayName: "MobileSearchBar__SearchInput",
  componentId: "oqoj09-1"
})([".mobile-search-input{border-radius:3px 0 0 3px;padding-right:0;height:", ";border-right:0;:focus{border-right:0;box-shadow:none;}}"], SEARCH_HEIGHT);
const StyledCancelButton = styled(UIClickable).withConfig({
  displayName: "MobileSearchBar__StyledCancelButton",
  componentId: "oqoj09-2"
})(["color:", ";right:8px;line-height:", ";margin-left:8px;"], OLAF, SEARCH_HEIGHT);
const StyledPlaceholderButton = styled(UIClickable).withConfig({
  displayName: "MobileSearchBar__StyledPlaceholderButton",
  componentId: "oqoj09-3"
})(["width:100%;"]);

function MobileSearchBar({
  ariaLabel,
  buttonFocused,
  cancelSearch,
  className,
  inputValue,
  onClear,
  onClick,
  onSearchFocus,
  onSearchUpdate,
  focused,
  onSearchSubmit,
  placeholder,
  use
}, ref) {
  const [searchInputValue, setSearchInputValue] = useState(inputValue || '');
  const prevInputValue = usePrevious(searchInputValue);
  const searchRef = useForwardedRef(ref);
  const buttonRef = useForwardedRef(ref);
  useEffect(() => {
    setSearchInputValue(inputValue || '');
  }, [inputValue]);
  useEffect(() => {
    if (!searchInputValue && prevInputValue) {
      onClear();
    }
  }, [searchInputValue, prevInputValue, onClear]);
  useEffect(() => {
    if (buttonFocused && buttonRef.current) {
      buttonRef.current.focus();
    }
  }, [buttonRef, buttonFocused]);
  const handleSubmitSearch = useCallback(evt => {
    evt.preventDefault();
    onSearchSubmit(searchInputValue);

    if (searchRef.current) {
      searchRef.current.blur();
    }
  }, [searchInputValue, searchRef, onSearchSubmit]);
  const focusSearchBar = useCallback(evt => {
    if (onSearchFocus) onSearchFocus(evt);
  }, [onSearchFocus]);
  const renderedPlaceholderSearchBar = useMemo(() => {
    return /*#__PURE__*/_jsxs(UIFlex, {
      children: [/*#__PURE__*/_jsx(StyledPlaceholderButton, {
        "aria-label": ariaLabel,
        buttonRef: buttonRef,
        onClick: onClick,
        children: /*#__PURE__*/_jsx(SearchInput, {
          clearable: false,
          icon: false,
          inputClassName: "mobile-search-input",
          placeholder: placeholder,
          tabIndex: -1,
          value: searchInputValue
        })
      }), /*#__PURE__*/_jsx(SearchButton, {
        "aria-label": unescapedText('ecosystemMarketplaceCore.storefront.searchBar.accessibleButtonName'),
        onClick: handleSubmitSearch,
        children: /*#__PURE__*/_jsx(UIIcon, {
          color: CALYPSO,
          name: "search"
        })
      })]
    });
  }, [ariaLabel, buttonRef, handleSubmitSearch, onClick, placeholder, searchInputValue]);

  if (use === 'placeholder') {
    return renderedPlaceholderSearchBar;
  }

  return /*#__PURE__*/_jsx(UIForm, {
    className: className,
    action: ".",
    onSubmit: handleSubmitSearch,
    children: /*#__PURE__*/_jsx(UIFormControl, {
      "aria-label": ariaLabel,
      children: /*#__PURE__*/_jsxs(UIFlex, {
        children: [/*#__PURE__*/_jsxs(UIFlex, {
          children: [/*#__PURE__*/_jsx(SearchInput, {
            clearable: focused,
            icon: false,
            inputClassName: "mobile-search-input",
            inputRef: searchRef,
            onChange: event => {
              const value = event.target.value;
              setSearchInputValue(value);

              if (onSearchUpdate) {
                onSearchUpdate(value);
              }
            },
            onFocus: focusSearchBar,
            placeholder: placeholder,
            value: searchInputValue
          }), /*#__PURE__*/_jsx(SearchButton, {
            "aria-label": unescapedText('ecosystemMarketplaceCore.storefront.searchBar.accessibleButtonName'),
            onClick: handleSubmitSearch,
            children: /*#__PURE__*/_jsx(UIIcon, {
              color: CALYPSO,
              name: "search"
            })
          })]
        }), focused && /*#__PURE__*/_jsx(StyledCancelButton, {
          onClick: cancelSearch,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "ecosystemMarketplaceCore.storefront.searchBar.cancelButton"
          })
        })]
      })
    })
  });
}

export default /*#__PURE__*/forwardRef(MobileSearchBar);